import { ReactNode, useRef, useState, useMemo, createElement } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as ArrowIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import { ReactComponent as FivetranIcon } from '@elementary/common-ui/assets/fivetran-icon.svg';
import { ReactComponent as ColumnsIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as DbtIcon } from '@elementary/common-ui/assets/dbt-icon.svg';
import { ReactComponent as PythonIcon } from '@elementary/common-ui/assets/python-lineage.svg';
import { ReactComponent as ElementIcon } from '@elementary/common-ui/assets/element-icon.svg';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/hex-icon-black.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/looker-icon.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/powerbi-icon.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/sigma-icon.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/lightdash-icon.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/tableau-icon.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/thoughtspot-icon.svg';
import {
  Chip,
  Flex,
  FlexColumn,
  RestrictedIndicator,
  SearchField,
} from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import useCriticalAssets from '@elementary/common-ui/hooks/useCriticalAssets';
import useHover from '@elementary/common-ui/hooks/useHover';
import {
  capitalize,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { ErrorOutline } from '@mui/icons-material';
import { Tooltip, Typography, CircularProgress } from '@mui/material';
import { Position, NodeProps, useEdges } from 'reactflow';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { getWarehouseIcon } from '@elementary/common-ui/utils/environments';
import { ReactComponent as SnowflakeIcon } from '@elementary/common-ui/assets/data-warehouses/snowflake.svg';
import {
  GRAPH_NODE_HEIGHT,
  RESTRICTED_NODE_HEIGHT,
  GRAPH_PARENT_NODE_HEIGHT,
  GRAPH_NODE_CHILDREN_HEADER_HEIGHT,
  GRAPH_NODE_SEARCH_AREA_HEIGHT,
  CHILD_NODE_PADDING,
  LINEAGE_PER_PARENT_COLUMNS_LIMIT,
} from './constants';
import { PlatformType, ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import GraphNodeHandle from './GraphNodeHandle';
import NodeContextMenu from './NodeContextMenu';

const semanticLayerIconMap: Record<string, ReactNode> = {
  snowflake: <SnowflakeIcon />,
};

const platformIconMap: Record<string, ReactNode> = {
  looker: <LookerIcon />,
  tableau: <TableauIcon />,
  powerbi: <PowerBiIcon />,
  sigma: <SigmaIcon />,
  thoughtspot: <ThoughtSpotIcon />,
  hex: <HexIcon />,
  lightdash: <LightdashIcon />,
};

enum ChildrenHeaderLabel {
  Element = 'element',
  Insight = 'insight',
  Cell = 'cell',
  ChartTile = 'chart tile',
  Column = 'column',
}

const childrenHeaderLabelMap = (
  node?: ReactFlowNodeData,
): ChildrenHeaderLabel | string => {
  const { platform, subType, type } = node?.metadata || {};

  if (type === 'semantic_view') {
    if (node?.parentNode) {
      return subType ?? ChildrenHeaderLabel.Element;
    }
    return ChildrenHeaderLabel.Element;
  }
  if (platform?.toLowerCase() === PlatformType.Sigma && subType === 'page') {
    return ChildrenHeaderLabel.Element;
  }
  if (
    platform?.toLowerCase() === PlatformType.ThoughtSpot &&
    subType === 'liveboard'
  ) {
    return ChildrenHeaderLabel.Insight;
  }
  if (platform?.toLowerCase() === PlatformType.Hex && subType === 'project') {
    return ChildrenHeaderLabel.Cell;
  }
  if (
    platform?.toLowerCase() === PlatformType.Lightdash &&
    subType === 'dashboard'
  ) {
    return ChildrenHeaderLabel.ChartTile;
  }
  return ChildrenHeaderLabel.Column;
};

const Wrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isFiltered?: boolean;
  $isRootNode?: boolean;
  $isSource?: boolean;
  $isExposure?: boolean;
  $isSemantic?: boolean;
  $isExpanded?: boolean;
  $isOnHighlightPath?: boolean;
  $height: number;
  $width: number;
  $isParent?: boolean;
  $isRestricted?: boolean;
}>`
  background: ${({ theme }) => theme.palette.common.white};
  height: ${({ $height, $isExpanded, $isParent, $isRestricted }) =>
    $isExpanded
      ? $height
      : $isRestricted
      ? RESTRICTED_NODE_HEIGHT
      : $isParent
      ? GRAPH_PARENT_NODE_HEIGHT + 4
      : GRAPH_NODE_HEIGHT}px;
  width: ${({ $width }) => $width}px;
  border-radius: 6px;
  overflow: hidden;

  border: ${({
    theme,
    $isRootNode,
    $isFiltered,
    $isSource,
    $isExposure,
    $isSemantic,
    $isHighlighted,
    $isOnHighlightPath,
    $isRestricted,
  }) =>
    `${
      $isHighlighted || $isOnHighlightPath
        ? '2px solid'
        : $isRestricted
        ? '2px dashed'
        : '2px solid'
    } ${
      $isHighlighted || $isOnHighlightPath
        ? theme.palette.common.blue7
        : $isRestricted
        ? theme.palette.common.gray4
        : $isRootNode
        ? theme.palette.common.magenta
        : $isFiltered
        ? theme.palette.common.pink
        : $isSource
        ? theme.palette.common.blue2
        : $isExposure
        ? theme.palette.common.purple
        : $isSemantic
        ? theme.palette.common.green
        : theme.palette.common.gray14
    }`};
`;

const Header = styled(FlexColumn)<{
  $isRootNode?: boolean;
  $isFiltered?: boolean;
  $isSource?: boolean;
  $isExposure?: boolean;
  $isSemantic?: boolean;
  $isRestricted?: boolean;
}>`
  height: ${({ $isRestricted }) =>
    $isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}px;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${({
    theme,
    $isRootNode,
    $isSource,
    $isExposure,
    $isFiltered,
    $isSemantic,
    $isRestricted,
  }) =>
    $isRestricted
      ? theme.palette.common.gray3
      : $isRootNode
      ? theme.palette.common.lightMagenta
      : $isFiltered
      ? theme.palette.common.lightPink
      : $isSource
      ? theme.palette.common.lightBlue4
      : $isExposure
      ? theme.palette.common.lightPurple
      : $isSemantic
      ? theme.palette.common.lightTurqoise
      : theme.palette.common.white};

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray10};
  }
`;

const NodeDetails = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TypeWrapper = styled(Flex)`
  gap: 8px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const NodeTitle = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`;

const ChildrenHeader = styled(Flex)<{
  $isExpanded?: boolean;
  $isSource?: boolean;
  $isExposure?: boolean;
  $isSemantic?: boolean;
  $isRootNode?: boolean;
  $isFiltered?: boolean;
  $disabled?: boolean;
}>`
  height: ${GRAPH_NODE_CHILDREN_HEADER_HEIGHT}px;
  align-items: center;
  padding: 0 16px;
  gap: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  ${({ theme, $isExpanded }) =>
    $isExpanded && `background: ${theme.palette.common.gray10}`};

  &:hover {
    background: ${({ theme, $disabled }) =>
      $disabled ? 'inherit' : theme.palette.common.gray10};
  }

  border-top: ${({
    theme,
    $isSource,
    $isExposure,
    $isSemantic,
    $isRootNode,
    $isFiltered,
  }) =>
    `1px solid ${
      $isRootNode
        ? theme.palette.common.magenta
        : $isFiltered
        ? theme.palette.common.pink
        : $isSource
        ? theme.palette.common.blue2
        : $isExposure
        ? theme.palette.common.purple
        : $isSemantic
        ? theme.palette.common.green
        : theme.palette.common.gray14
    }`};
`;

const ChildrenCountWrapper = styled(Flex)`
  align-items: center;
  gap: 4.8px;
  min-width: 0;
  flex: 1;
`;

const ArrowIconStyled = styled(ArrowIcon)<{ $flip: boolean }>`
  width: 10px;
  margin-top: 1px;
  transform: ${({ $flip }) => ($flip ? 'rotate(-180deg)' : undefined)};
  transition: transform 0.1s;
`;

const ColumnIconStyled = styled(ColumnsIcon)`
  width: 16px;
  height: 21px;
`;

const DbtIconStyled = styled(DbtIcon)`
  path {
    fill: ${({ theme }) => theme.palette.common.gray4};
  }
`;

const ColumnLimitWarningIcon = styled(ErrorOutline)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.palette.warning.main};
`;

const ColumnsLoadingArea = styled(FlexColumn)`
  height: ${GRAPH_NODE_SEARCH_AREA_HEIGHT}px;
  justify-content: center;
  align-items: center;
  padding: 0 ${CHILD_NODE_PADDING}px;
`;

const GraphNode = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data, dragging } = node;
  const edges = useEdges();
  const hasPermission = useEnvPermission();

  const hasIncomingEdges = edges.some((e) => e.target === id && !e.hidden);
  const hasOutgoingEdges = edges.some((e) => e.source === id && !e.hidden);

  const { highlightedNode, exceededColumnLimitAssets, isControlled } =
    useLineageGraphContext();
  const { selectedModelColumn } = useSelectedTreeItem();

  const ref = useRef<any>();
  const childrenHeaderRef = useRef<any>();
  const searchFieldRef = useRef<any>();
  const isHover = useHover(ref);
  const isChildrenHeaderHover = useHover(childrenHeaderRef);
  const isSearchFieldHover = useHover(searchFieldRef);

  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const hasTestReadScope = hasPermission('VIEW_TESTS');

  const handleMenuOpen = (e: any) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const handleMenuClose = () => {
    setMenuPos(null);
  };

  const isChildrenFilteredByUrlColumn =
    !!selectedModelColumn && selectedModelColumn !== id;

  const nodeTypeLabel = useMemo(() => {
    if (data.metadata.type === 'semantic_view' && data.parentNode) {
      return 'element';
    }
    return data.metadata.subType || data.metadata.type || 'model';
  }, [data]);

  const isChildWithChildren = !!node.data.parentNode && !!node.data.children;

  const childrenHeaderLabel = childrenHeaderLabelMap(data);

  const childrenHeaderIcon =
    data.metadata.subType === 'page' ||
    data.metadata.subType === 'project' ||
    (data.metadata.subType === 'dashboard' &&
      data.metadata.platform?.toLowerCase() === PlatformType.Lightdash) ? (
      <ElementIcon width={16} />
    ) : (
      <ColumnIconStyled />
    );

  const isElement =
    data.metadata.subType === 'element' ||
    data.metadata.subType === 'cell' ||
    data.metadata.subType === 'chart_tile';

  const isCell = data.metadata.subType === 'cell';

  const { criticalAssetsData, setIsCriticalAssets, hasSetPermissions } =
    useCriticalAssets({
      isCritical: true,
    });
  const isCriticalAsset = !!criticalAssetsData.data?.find(
    (asset) => asset.id === id,
  );

  const onSetIsCriticalAssets = () => {
    setIsCriticalAssets.setIsCriticalAssetsMutation({
      assetsIds: [id],
      isCriticalValue: !isCriticalAsset,
    });
  };

  const nodeIcon = useMemo(() => {
    if (isElement || isCell) return <ElementIcon />;

    if (data.metadata.type === 'semantic_view') {
      const platform =
        data.metadata.platform || data.parentNode?.platform || '';
      return semanticLayerIconMap[platform.toLowerCase()] ?? null;
    }

    const platformIcon =
      platformIconMap[data.metadata.platform?.toLowerCase() || ''];
    if (platformIcon) return platformIcon;

    if (data.metadata?.sourceType === 'python_sdk') {
      return <PythonIcon />;
    }

    if (data.metadata?.sourceType?.toLowerCase() === 'fivetran') {
      return <FivetranIcon />;
    }

    if (data.metadata?.sourceType && data.metadata?.sourceType !== 'dbt') {
      const platformIcon = createElement(
        getWarehouseIcon(data.metadata.dataPlatform?.toLowerCase() || ''),
      );
      if (platformIcon) return platformIcon;
    }

    return <DbtIconStyled />;
  }, [data.metadata, data.parentNode?.platform, isElement, isCell]);

  const isSemantic = data.metadata.type === 'semantic_view';
  const isRestricted = data.metadata.isRestricted === true;
  const hasExceededColumnLimit = exceededColumnLimitAssets.has(id);

  const showConnectedColumnsOnly = !!data.isColumnHighlightFiltered;

  // `columnsCount` only counts column descendants (0 when children are
  // elements), so prefer the loaded children count when present.
  const childrenCount = showConnectedColumnsOnly
    ? data.children?.length ?? 0
    : data.children?.length ||
      data.columnsCount ||
      data.metadata.columnsCount ||
      0;

  const isExpandable = !showConnectedColumnsOnly || childrenCount > 0;

  // Highlight only filters column children (not elements), so flag as filtered
  // only when children are columns and fewer are shown than the full count.
  const fullColumnCount = data.columnsCount ?? data.metadata.columnsCount ?? 0;
  const isChildrenFilteredByHighlightColumn =
    showConnectedColumnsOnly &&
    childrenHeaderLabel === ChildrenHeaderLabel.Column &&
    childrenCount < fullColumnCount;

  const isChildrenFiltered =
    isChildrenFilteredByUrlColumn || isChildrenFilteredByHighlightColumn;

  const showChildrenSection =
    !isRestricted &&
    (showConnectedColumnsOnly
      ? true
      : childrenCount > 0 || !!node.data.children || !!data.isColumnsLoading);

  const isVisuallyExpanded = !!data.isExpanded || !!data.isColumnsLoading;

  // While loading, no children are merged yet so data.height collapses —
  // reserve room for the header, columns header and spinner.
  const loadingExpandedHeight =
    GRAPH_NODE_HEIGHT +
    GRAPH_NODE_CHILDREN_HEADER_HEIGHT +
    GRAPH_NODE_SEARCH_AREA_HEIGHT +
    4;

  const wrapperHeight =
    data.isColumnsLoading && !data.children
      ? loadingExpandedHeight
      : data.height;

  return (
    <Wrapper
      ref={ref}
      $isHighlighted={id === highlightedNode?.id}
      $isRootNode={data.isRootNode}
      $isFiltered={data.isFiltered}
      $isSource={data.metadata.type === 'source'}
      $isExposure={data.metadata.type === 'exposure'}
      $isSemantic={isSemantic}
      $height={wrapperHeight}
      $isExpanded={isVisuallyExpanded}
      $isOnHighlightPath={data.isOnHighlightPath}
      $width={data.width}
      $isParent={showChildrenSection}
      $isRestricted={isRestricted}
      onContextMenu={isControlled ? undefined : handleMenuOpen}
    >
      <GraphNodeHandle
        type="source"
        position={Position.Right}
        $extendHandle={hasOutgoingEdges && isChildWithChildren}
        $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
        $nodeHeight={isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}
      />

      <FlexColumn
        sx={{
          height: '100%',
          width: '100%',
        }}
      >
        <Header
          $isRootNode={data.isRootNode}
          $isFiltered={data.isFiltered}
          $isSource={data.metadata.type === 'source'}
          $isExposure={data.metadata.type === 'exposure'}
          $isSemantic={isSemantic}
          $isRestricted={isRestricted}
        >
          {isRestricted ? (
            <RestrictedIndicator />
          ) : (
            <>
              <NodeDetails>
                <TypeWrapper>
                  {nodeIcon}
                  <Typography sx={{ fontSize: '12px', lineHeight: 'normal' }}>
                    {capitalize(nodeTypeLabel)}
                  </Typography>
                </TypeWrapper>

                {isCriticalAsset && <CriticalAssetIcon />}
              </NodeDetails>
              <NodeTitle>
                <OverflowTooltip
                  color={
                    highlightedNode?.id === id
                      ? 'common.magenta'
                      : 'common.black'
                  }
                  bold={highlightedNode?.id === id}
                  typographySx={{ lineHeight: 'normal' }}
                >
                  {data.metadata.label}
                </OverflowTooltip>

                {hasTestReadScope &&
                  (!!data.totals?.errors ||
                    !!data.totals?.warnings ||
                    !!data.totals?.passed ||
                    !!data.totals?.failures) && (
                    <Flex sx={{ alignItems: 'center', gap: 0.6, ml: 3 }}>
                      {data.totals?.passed > 0 && (
                        <Chip
                          role="alert"
                          aria-label="success"
                          label={data.totals.passed}
                          color="success"
                          size="small"
                        />
                      )}
                      {data.totals?.warnings > 0 && (
                        <Chip
                          role="alert"
                          aria-label="warning"
                          label={data.totals.warnings}
                          color="warning"
                          size="small"
                        />
                      )}
                      {(data.totals?.errors > 0 ||
                        data.totals?.failures > 0) && (
                        <Chip
                          role="alert"
                          aria-label="error"
                          label={
                            (data.totals.errors || 0) +
                            (data.totals.failures || 0)
                          }
                          color="error"
                          size="small"
                        />
                      )}
                    </Flex>
                  )}
              </NodeTitle>

              {!isControlled && (
                <NodeContextMenu
                  showMenuButton={
                    isHover &&
                    !dragging &&
                    !isChildrenHeaderHover &&
                    !isSearchFieldHover
                  }
                  node={node}
                  pos={menuPos}
                  onOpen={handleMenuOpen}
                  onClose={handleMenuClose}
                  onHighlightNodeClick={() => ref.current?.click()}
                  isCriticalAsset={isCriticalAsset}
                  onSetIsCriticalAssets={onSetIsCriticalAssets}
                  showCriticalAssetOption={hasSetPermissions}
                  showTestResults={hasTestReadScope}
                />
              )}
            </>
          )}
        </Header>

        {showChildrenSection && (
          <FlexColumn>
            <ChildrenHeader
              ref={childrenHeaderRef}
              id={isExpandable ? 'expand-button' : 'children-header-static'}
              $disabled={!isExpandable}
              $isExpanded={isVisuallyExpanded}
              $isSource={data.metadata.type === 'source'}
              $isExposure={data.metadata.type === 'exposure'}
              $isSemantic={isSemantic}
              $isRootNode={data.isRootNode}
              $isFiltered={data.isFiltered}
            >
              {childrenHeaderIcon}
              <ChildrenCountWrapper>
                <Typography noWrap>
                  {pluralizeString(
                    childrenCount,
                    `${
                      isChildrenFiltered ? 'filtered ' : ''
                    }${childrenHeaderLabel}`,
                    `${
                      isChildrenFiltered ? 'filtered ' : ''
                    }${childrenHeaderLabel}s`,
                  )}
                </Typography>
                {hasExceededColumnLimit && (
                  <Tooltip
                    title={`This node has too many columns. Showing up to ${LINEAGE_PER_PARENT_COLUMNS_LIMIT} columns.`}
                  >
                    <ColumnLimitWarningIcon />
                  </Tooltip>
                )}
              </ChildrenCountWrapper>
              {isExpandable && (
                <ArrowIconStyled
                  id="expand-button-icon"
                  $flip={isVisuallyExpanded}
                />
              )}
            </ChildrenHeader>

            {data.isColumnsLoading && (
              <ColumnsLoadingArea>
                <CircularProgress size={16} />
              </ColumnsLoadingArea>
            )}

            {data.isExpanded && !data.isColumnsLoading && (
              <FlexColumn
                ref={searchFieldRef}
                sx={{
                  height: `${GRAPH_NODE_SEARCH_AREA_HEIGHT}px`,
                  justifyContent: 'center',
                  paddingX: `${CHILD_NODE_PADDING}px`,
                }}
              >
                <SearchField
                  id={JSON.stringify({
                    elementId: 'search-field',
                    nodeId: node.id,
                  })}
                  fullWidth
                />
              </FlexColumn>
            )}
          </FlexColumn>
        )}
      </FlexColumn>

      <GraphNodeHandle
        type="target"
        position={Position.Left}
        $extendHandle={hasIncomingEdges && isChildWithChildren}
        $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
        $nodeHeight={isRestricted ? RESTRICTED_NODE_HEIGHT : GRAPH_NODE_HEIGHT}
      />
    </Wrapper>
  );
};

export default GraphNode;
